import React, {FC, useState, useMemo, useEffect} from "react";
import {Button, Modal, InputNumber, Input, Checkbox, message, Tooltip} from "antd";
import {
    useCancelMolliePaymentFromAdminMutation,
    useRefreshMolliePaymentStatusFromAdminMutation,
    useCaptureMolliePaymentFromAdminMutation,
    useCreateNewMolliePaymentAttemptFromAdminMutation,
    useSendMolliePaymentLinkFromAdminMutation,
    useRefundMolliePaymentFromAdminMutation,
    useRecordExternalPaymentForScheduledPaymentMutation,
    GetBookingByIdWithCourseLessonBookingsDocument,
} from "../../../../generated/graphql";
import {actionMappingForState, MollieActionKey} from "./helpers/actionMappingForState";
import {getDisplayMollieStatus, isMollieBanktransfer} from "./helpers/mollieFlowKind";
import {REFUND_EPSILON, computeRefundSummary} from "./helpers/refundSummary";
import {buildMollieDashboardUrl} from "../../../../helpers/buildMollieDashboardUrl";
import {renderAmount} from "../../../../helpers/renderAmount";
import RefundAmountFields from "../../../../views/RefundPanel/RefundAmountFields";
import {
    RecordExternalPaymentModal,
    RecordExternalPaymentValues,
} from "./RecordExternalPaymentModal";

const css = require("./MolliePaymentSection.less");

// Phase 23 / Plan 23-05 / BDSEC-04 — ActionBar mit state-driven Buttons.
//
// Wired Mutations (alle 6, mit refetchQueries auf Booking-Query):
//   - cancelMolliePaymentFromAdmin       → Aktion "Stornieren" (open / authorized)
//   - refreshMolliePaymentStatusFromAdmin → Aktion "Status aktualisieren" (pending;
//     seit W-275 auch paid — zieht dort die real genutzte Mollie-Methode nach)
//   - captureMolliePaymentFromAdmin       → Aktionen "Erfassen voll/teil" (authorized)
//   - createNewMolliePaymentAttemptFromAdmin → "Neuen Zahlungslink erstellen" (failed/expired/canceled)
//   - sendMolliePaymentLinkFromAdmin       → "Link erneut senden" (open) — REUSE D-05
//   - refundMolliePaymentFromAdmin         → "Erstatten" / "Weiter erstatten" (paid)
//
// T-23-06: Alle Buttons disabled während laufender Mutation (aggregierter
// loading-State). Doppelklick wird so verhindert.

export interface ActionBarAttemptShape {
    id: string;
    molliePaymentId: string;
    status: string;
    mollieMethod?: string | null;
    scheduledPaymentId: string;
    amount?: number | null;
    checkoutUrl?: string | null;
    paymentUrl?: string | null;
    linkExpiresAt?: string | Date | null;
    refunds?: ReadonlyArray<{amount?: number | null; status?: string | null}> | null;
}

export type ActionBarProps = {
    attempt: ActionBarAttemptShape;
    bookingId: string;
    refetchBooking: () => Promise<unknown>;
    // Doppel-Gutschrift-Schutz: True, wenn die Buchung storniert ist. Eine stornierte
    // Buchung kann durch eine Umbuchung entstanden sein — dann wurde das gezahlte
    // Guthaben bereits auf die neue Buchung übertragen, und eine Erstattung hier
    // führt zur Doppel-Gutschrift. Deshalb wird die Erstattung bei stornierten
    // Buchungen mit einer ausdrücklichen Warn-Bestätigung versehen (kein harter
    // Block — ein normaler Storno "ohne Refund" darf weiterhin nachträglich erstattet
    // werden).
    isBookingCanceled?: boolean;
    /** Person nach DSGVO-Löschantrag gelöscht: nur noch Aktionen, die der Server erlaubt (siehe HIDDEN_ACTIONS_WHEN_ATTENDEE_ERASED). */
    attendeeErased?: boolean;
    /**
     * Wartelistenplatz → keine neuen Zahlungsversuche und kein Link-Versand
     * (Incident 18.08.2026; analog `isBookingCanceled` für newAttempt/resend).
     */
    isWaitlistBooking?: boolean;
    /**
     * Darstellungs-Variante.
     *   "full"        — komplette Leiste (Primary- + Secondary-Gruppe). Default,
     *                   genutzt im aufgeklappten Posten-Detail.
     *   "primaryOnly" — NUR die erste Primäraktion als kompakter Button. Genutzt
     *                   in der Aktionsspalte der zugeklappten Posten-Zeile, damit
     *                   dort genau EINE Aktion steht — ohne die Mutations-,
     *                   Modal- und Guard-Logik zu duplizieren.
     */
    variant?: "full" | "primaryOnly";
};

// Warntext für Erstattungen auf stornierten (möglicherweise umgebuchten) Buchungen.
export const CANCELED_REFUND_DOUBLE_CREDIT_WARNING =
    "Achtung: Diese Buchung ist storniert. Wurde der Teilnehmer in einen anderen Kurs umgebucht, "
    + "ist das gezahlte Guthaben bereits auf die neue Buchung übertragen worden — eine Erstattung hier "
    + "führt dann zu einer Doppel-Gutschrift (Geld zurück an den Kunden UND verrechnetes Guthaben auf der "
    + "neuen Buchung). Bitte nur fortfahren, wenn KEINE Umbuchung erfolgt ist.";

const ACTION_LABELS: Record<MollieActionKey, string> = {
    // Phase 26 / Fix #4 OOS-3 — Default-Label = "Zahlungslink versenden".
    // Wechselt nach erstem manuellen Versand (sendMolliePaymentLinkFromAdmin)
    // in der gleichen Session zu "Link erneut senden". Siehe `RESEND_LABEL_*`
    // weiter unten + State `hasManuallySent`.
    resend: "Zahlungslink versenden",
    copy: "Link kopieren",
    cancel: "Stornieren",
    refresh: "Status aktualisieren",
    captureFull: "Vollständig erfassen",
    capturePartial: "Teilweise erfassen",
    refundPartial: "Erstatten",
    refundMore: "Weiter erstatten",
    showChargebacks: "Chargebacks anzeigen",
    openDashboard: "Mollie-Dashboard öffnen",
    newAttempt: "Neuen Zahlungslink erstellen",
    recordExternalPayment: "Außerhalb Mollie bezahlt erfassen",
};

// Phase 26 / Fix #4 OOS-3 — Resend-Label + Tooltip-Texte für die zwei Zustände.
// Hintergrund (UAT 2026-05-17): Im Empty-State erzeugt der Admin via
// `createNewMolliePaymentAttempt` einen neuen Attempt — dieser Aufruf
// versendet KEINE Mail (sendet nur den Mollie-API-Call). Der Button
// "Link erneut senden" wirkt daher missverständlich, solange der Admin
// noch nicht einmal aktiv versendet hat.
// Wir tracken den ersten manuellen Versand per Session-State `hasManuallySent`.
// Persistenz über Reload ist OUT OF SCOPE (würde Backend-Persistenz
// in PaymentActionLog oder am MolliePaymentAttempt voraussetzen — separates
// Backend-Phase 26 Folge-Ticket).
const RESEND_LABEL_FIRST = "Zahlungslink versenden";
const RESEND_LABEL_REPEAT = "Link erneut senden";
const RESEND_TOOLTIP_FIRST =
    "Sendet den Zahlungslink jetzt per E-Mail an den Bucher.";
const RESEND_TOOLTIP_REPEAT =
    "Du hast den Zahlungslink in dieser Sitzung bereits manuell versendet. Diese Aktion sendet die E-Mail nochmals — z.B. falls der Bucher sie nicht erhalten oder verlegt hat.";
const BANKTRANSFER_RESEND_LABEL_FIRST = "Zahlungsdaten senden";
const BANKTRANSFER_RESEND_LABEL_REPEAT = "Zahlungsdaten erneut senden";
const BANKTRANSFER_RESEND_TOOLTIP_FIRST =
    "Sendet die Mollie-Zahlungsdaten jetzt per E-Mail an den Bucher.";
const BANKTRANSFER_RESEND_TOOLTIP_REPEAT =
    "Du hast die Zahlungsdaten in dieser Sitzung bereits manuell versendet. Diese Aktion sendet die E-Mail nochmals.";

// Capture-Actions (Erfassen von authorisierten Zahlungen). Für banktransfer
// nicht zutreffend — siehe Filter in der ActionBar (WS-4).
const CAPTURE_ACTIONS: ReadonlyArray<MollieActionKey> = ["captureFull", "capturePartial"];

function isCaptureAction(action: MollieActionKey): boolean {
    return CAPTURE_ACTIONS.indexOf(action) !== -1;
}

// B-6967: Erzeuger-/Versand-Aktionen, die bei einer stornierten Buchung nicht
// mehr angeboten werden dürfen (würden den Kunden zur Zahlung einer stornierten
// Buchung auffordern). Cancel/Refresh/Refund/Dashboard bleiben erhalten.
const HIDDEN_ACTIONS_WHEN_BOOKING_CANCELED: ReadonlyArray<MollieActionKey> = ["newAttempt", "resend"];

// Nach DSGVO-Löschantrag lehnt der Server neue Zahlungsversuche, Link-Versand, Einzug autorisierter
// Zahlungen und Erstattungen ab (erasureGuard). Abbrechen, Status aktualisieren, Chargebacks, Dashboard
// und das Erfassen eines Zahlungseingangs bleiben, damit die Buchhaltung abschließbar ist.
const HIDDEN_ACTIONS_WHEN_ATTENDEE_ERASED: ReadonlyArray<MollieActionKey> = ["newAttempt", "resend", "copy", "captureFull", "capturePartial", "refundPartial", "refundMore"];

export const ActionBar: FC<ActionBarProps> = ({attempt, bookingId, refetchBooking, isBookingCanceled, isWaitlistBooking, attendeeErased, variant}) => {
    const isPrimaryOnly = variant === "primaryOnly";
    const isBanktransfer = isMollieBanktransfer(attempt.mollieMethod);
    const refetchOpts = useMemo(
        () => ({
            refetchQueries: [
                {
                    query: GetBookingByIdWithCourseLessonBookingsDocument,
                    variables: {id: bookingId},
                },
                // Versandhistorie (CommunicationHistoryBooking) sofort mit aktualisieren
                "GetEmailNotificationsByBookingId",
            ],
            awaitRefetchQueries: true,
        }),
        [bookingId],
    );

    const [cancelMutation, {loading: cancelLoading}] = useCancelMolliePaymentFromAdminMutation(
        refetchOpts,
    );
    const [
        refreshMutation,
        {loading: refreshLoading},
    ] = useRefreshMolliePaymentStatusFromAdminMutation(refetchOpts);
    const [
        captureMutation,
        {loading: captureLoading},
    ] = useCaptureMolliePaymentFromAdminMutation(refetchOpts);
    const [
        createNewMutation,
        {loading: createNewLoading},
    ] = useCreateNewMolliePaymentAttemptFromAdminMutation(refetchOpts);
    const [
        sendLinkMutation,
        {loading: sendLinkLoading},
    ] = useSendMolliePaymentLinkFromAdminMutation(refetchOpts);
    const [
        refundMutation,
        {loading: refundLoading},
    ] = useRefundMolliePaymentFromAdminMutation(refetchOpts);
    const [
        recordExternalMutation,
        {loading: recordExternalLoading},
    ] = useRecordExternalPaymentForScheduledPaymentMutation(refetchOpts);

    const isLoading =
        cancelLoading ||
        refreshLoading ||
        captureLoading ||
        createNewLoading ||
        sendLinkLoading ||
        refundLoading ||
        recordExternalLoading;

    // ─── Capture-Partial-Modal-State ──────────────────────────────────────
    const [capturePartialOpen, setCapturePartialOpen] = useState<boolean>(false);
    const [capturePartialAmount, setCapturePartialAmount] = useState<number | null>(null);

    // ─── Refund-Modal-State ───────────────────────────────────────────────
    // Ein Modal mit IMMER sichtbarem, leerem Betragsfeld. Früher gab es zusätzlich
    // "Vollständig erstatten" ohne Betragsfeld: Nübad trug dort „25 € einbehalten"
    // ins Grund-Feld ein und erstattete trotzdem alles (6 Fälle, u. a. B-7027).
    const [refundModalOpen, setRefundModalOpen] = useState<boolean>(false);
    const [refundAmount, setRefundAmount] = useState<number | null>(null);
    const [refundReason, setRefundReason] = useState<string>("");
    // Default: informieren (AN). Spiegelt den Server-Default (notifyBooker !== false).
    const [refundNotifyBooker, setRefundNotifyBooker] = useState<boolean>(true);

    // ─── "Außerhalb Mollie bezahlt erfassen"-Modal-State ──────────────────
    const [recordOpen, setRecordOpen] = useState<boolean>(false);

    // ─── Phase 26 / Fix #4 OOS-3 — Resend-Label-Tracking (Session-State) ──
    // Wird beim ersten erfolgreichen `handleResend` true. Reset bei
    // Attempt-Wechsel (z.B. nach `newAttempt`/`createNewMolliePaymentAttempt`)
    // über `attempt.id`-Reset (siehe useEffect).
    const [hasManuallySent, setHasManuallySent] = useState<boolean>(false);
    useEffect(() => {
        setHasManuallySent(false);
    }, [attempt.id]);

    // Einmalige, konsistente Refund-Lage (Status-gefiltert) — wird für Mapping,
    // handleRefundFull, Modal-max und Confirm-Guard wiederverwendet.
    const refundSummary = useMemo(() => computeRefundSummary(attempt), [attempt]);
    const {remaining, totalRefunded, isFullyRefunded, hasPartialRefund} = refundSummary;
    const actionStatus = getDisplayMollieStatus(
        attempt.status,
        attempt.mollieMethod,
        attempt.linkExpiresAt,
    );
    const rawActions = actionMappingForState(actionStatus, hasPartialRefund, isFullyRefunded);
    // WS-4: Für SEPA-Überweisung werden generell keine Capture-Buttons angeboten —
    // banktransfer hat keine authorized/Capture-Phase (anders als Klarna/Karten),
    // ein defensiv möglicher authorized-Status darf daher keine "Erfassen voll/
    // teilweise" zeigen. Zudem entfällt der "Link kopieren"-Button (der Bucher
    // erhält die Zahlungsdaten per E-Mail, nicht über einen Checkout-Link).
    const banktransferFiltered = isBanktransfer
        ? {
            primary: rawActions.primary.filter((action) => !isCaptureAction(action)),
            secondary: rawActions.secondary.filter(
                (action) => action !== "copy" && !isCaptureAction(action),
            ),
        }
        : rawActions;
    // B-6967 / Incident 18.08.2026: Bei stornierter Buchung oder Warteliste dürfen
    // keine neuen Zahlungsversuche erzeugt (newAttempt) oder Zahlungsdaten/Links
    // versendet (resend) werden. Cancel, Refresh, Refund und "Mollie-Dashboard
    // öffnen" bleiben erhalten.
    const hidePaymentRequestActions = Boolean(isBookingCanceled) || Boolean(isWaitlistBooking);
    const hiddenActions: Array<MollieActionKey> = [
        ...(hidePaymentRequestActions ? HIDDEN_ACTIONS_WHEN_BOOKING_CANCELED : []),
        ...(attendeeErased ? HIDDEN_ACTIONS_WHEN_ATTENDEE_ERASED : []),
    ];
    const actions = hiddenActions.length
        ? {
            primary: banktransferFiltered.primary.filter((action) => hiddenActions.indexOf(action) === -1),
            secondary: banktransferFiltered.secondary.filter((action) => hiddenActions.indexOf(action) === -1),
        }
        : banktransferFiltered;

    function labelForAction(action: MollieActionKey): string {
        if (isBanktransfer && action === "newAttempt") {
            return "Neuen Zahlungsversuch erstellen";
        }
        return ACTION_LABELS[action];
    }

    async function safeRun(
        label: string,
        fn: () => Promise<unknown>,
    ): Promise<void> {
        try {
            await fn();
            await refetchBooking();
            message.success(`${label} erfolgreich`, 3);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            Modal.error({
                title: `${label} fehlgeschlagen`,
                content: msg,
            });
        }
    }

    function handleCancel() {
        Modal.confirm({
            title: "Mollie-Zahlung wirklich stornieren?",
            content:
                "Die Zahlung wird bei Mollie als 'canceled' markiert. Diese Aktion kann nicht rückgängig gemacht werden.",
            okText: "Stornieren",
            okType: "danger",
            cancelText: "Abbrechen",
            onOk: () =>
                safeRun("Stornierung", () =>
                    cancelMutation({
                        variables: {scheduledPaymentId: attempt.scheduledPaymentId},
                    }),
                ),
        });
    }

    function handleRefresh() {
        safeRun("Status-Aktualisierung", () =>
            refreshMutation({
                variables: {scheduledPaymentId: attempt.scheduledPaymentId},
            }),
        );
    }

    function handleCaptureFull() {
        safeRun("Vollständige Erfassung", () =>
            captureMutation({
                variables: {scheduledPaymentId: attempt.scheduledPaymentId},
            }),
        );
    }

    function handleCapturePartialOpen() {
        // Pre-fill mit attempt.amount.
        setCapturePartialAmount(attempt.amount || null);
        setCapturePartialOpen(true);
    }

    function handleCapturePartialConfirm() {
        if (!capturePartialAmount || capturePartialAmount <= 0) {
            message.error("Bitte einen gültigen Betrag eingeben.");
            return;
        }
        setCapturePartialOpen(false);
        safeRun("Teilweise Erfassung", () =>
            captureMutation({
                variables: {
                    scheduledPaymentId: attempt.scheduledPaymentId,
                    amount: capturePartialAmount,
                },
            }),
        );
    }

    function handleNewAttempt() {
        Modal.confirm({
            title: isBanktransfer ? "Neuen Zahlungsversuch erstellen?" : "Neuen Zahlungslink erstellen?",
            content:
                "Ein neuer Mollie-Payment-Versuch wird erzeugt. Der bisherige Versuch wird als 'nicht aktuell' markiert.",
            okText: isBanktransfer ? "Neuen Versuch erstellen" : "Neuen Link erstellen",
            cancelText: "Abbrechen",
            onOk: () =>
                safeRun(isBanktransfer ? "Neuer Zahlungsversuch" : "Neuer Zahlungslink", () =>
                    createNewMutation({
                        variables: {scheduledPaymentId: attempt.scheduledPaymentId},
                    }),
                ),
        });
    }

    async function handleResend() {
        // Phase 26 / Fix #4 OOS-3 — nach erfolgreichem Versand auf "erneut"-Label
        // umschalten. `safeRun` swallowed Errors über Modal.error; wir nutzen
        // daher explizit den Mutation-Promise um nur bei Erfolg umzuschalten.
        try {
            await sendLinkMutation({
                variables: {scheduledPaymentId: attempt.scheduledPaymentId},
            });
            await refetchBooking();
            setHasManuallySent(true);
            message.success(isBanktransfer ? "Zahlungsdaten-Versand erfolgreich" : "Link-Versand erfolgreich", 3);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            Modal.error({
                title: isBanktransfer ? "Zahlungsdaten-Versand fehlgeschlagen" : "Link-Versand fehlgeschlagen",
                content: msg,
            });
        }
    }

    function handleCopy() {
        const url = attempt.checkoutUrl || attempt.paymentUrl;
        if (!url) {
            message.warning("Kein Checkout-Link zum Kopieren verfügbar.");
            return;
        }
        if (
            typeof navigator !== "undefined" &&
            navigator.clipboard &&
            navigator.clipboard.writeText
        ) {
            navigator.clipboard
                .writeText(url)
                .then(() => message.success("Link kopiert", 2))
                .catch(() => message.error("Kopieren fehlgeschlagen", 2));
        } else {
            message.error("Clipboard nicht verfügbar", 2);
        }
    }

    function handleOpenDashboard() {
        // T-23-Dashboard-XSS: buildMollieDashboardUrl nutzt encodeURIComponent;
        // window.open mit noopener,noreferrer.
        const url = buildMollieDashboardUrl(attempt.molliePaymentId);
        window.open(url, "_blank", "noopener,noreferrer");
    }

    // Öffnet das Erstattungs-Modal mit leerem Betrag: der Betrag muss bewusst
    // eingegeben oder per "Restbetrag übernehmen" gesetzt werden.
    function openRefundModal() {
        setRefundAmount(null);
        setRefundReason("");
        setRefundNotifyBooker(true);
        setRefundModalOpen(true);
    }

    function closeRefundModal() {
        setRefundModalOpen(false);
    }

    function handleRefundOpen() {
        openRefundModal();
    }

    function handleRefundConfirm() {
        const amount = refundAmount;
        if (!amount || amount <= 0) {
            message.error("Bitte einen gültigen Betrag eingeben.");
            return;
        }
        // Cap auf Restbetrag statt Zahlbetrag — verhindert Backend-Guard-3c-Fehler
        // bei bereits (teil-)erstatteten Zahlungen.
        if (amount > remaining + REFUND_EPSILON) {
            message.error("Betrag überschreitet den erstattbaren Restbetrag.");
            return;
        }
        closeRefundModal();
        // Grund nur mitsenden, wenn nicht leer (Backend-Spalte varchar(500));
        // notifyBooker steuert die Bucher-Bestätigungsmail (Default AN).
        const trimmedReason = refundReason.trim();
        const variables: {
            scheduledPaymentId: string;
            amount: number;
            reason?: string;
            notifyBooker: boolean;
        } = {
            scheduledPaymentId: attempt.scheduledPaymentId,
            amount,
            notifyBooker: refundNotifyBooker,
        };
        if (trimmedReason) {
            variables.reason = trimmedReason;
        }
        safeRun("Erstattung", () =>
            refundMutation({variables}),
        );
    }

    function handleShowChargebacks() {
        // Chargebacks-View ist out-of-scope für Plan 23-05 (Plan 23-06 / Phase 24).
        // Stub: Hinweis-Modal.
        Modal.info({
            title: "Chargebacks",
            content: "Die Chargeback-Detailansicht wird in einem späteren Plan integriert.",
        });
    }

    function handleRecordOpen() {
        setRecordOpen(true);
    }

    function handleRecordConfirm(values: RecordExternalPaymentValues) {
        // Es wird IMMER der volle offene Ratenbetrag erfasst — daher KEIN Betrag
        // an die Mutation. Das Backend flippt die Rate auf "Manuell" (bezahlt) und
        // entwertet den nicht mehr nutzbaren Mollie-Zahlungslink.
        setRecordOpen(false);
        safeRun("Externe Zahlung erfasst", () =>
            recordExternalMutation({
                variables: {
                    input: {
                        id: attempt.scheduledPaymentId,
                        paymentTransferType: values.paymentTransferType,
                        paymentDone: values.paymentDone,
                        reference: values.reference,
                    },
                },
            }),
        );
    }

    function renderActionButton(action: MollieActionKey, type: "primary" | "default"): React.ReactNode {
        const buttonType = type === "primary" ? "primary" : "default";
        const label = labelForAction(action);
        const dataTestId = `mollie-action-${action}`;

        switch (action) {
            case "resend":
                // Phase 26 / Fix #4 OOS-3 — Conditional Label + Tooltip:
                // - Vor erstem manuellen Versand: "Zahlungslink versenden"
                //   (klare Erstversand-Semantik — der `createNewMolliePaymentAttempt`-
                //   Pfad versendet keine Mail, nur den Mollie-API-Call).
                // - Nach erstem manuellem Versand (Session): "Link erneut senden".
                // Persistenz über Reload OUT OF SCOPE — siehe RESEND_LABEL_*-Kommentar.
                return (
                    <Tooltip
                        key={action}
                        title={
                            hasManuallySent
                                ? (isBanktransfer ? BANKTRANSFER_RESEND_TOOLTIP_REPEAT : RESEND_TOOLTIP_REPEAT)
                                : (isBanktransfer ? BANKTRANSFER_RESEND_TOOLTIP_FIRST : RESEND_TOOLTIP_FIRST)
                        }
                    >
                        <Button
                            type={buttonType}
                            size="small"
                            disabled={isLoading}
                            onClick={handleResend}
                            data-testid={dataTestId}
                            data-resend-state={hasManuallySent ? "repeat" : "first"}
                        >
                            {hasManuallySent
                                ? (isBanktransfer ? BANKTRANSFER_RESEND_LABEL_REPEAT : RESEND_LABEL_REPEAT)
                                : (isBanktransfer ? BANKTRANSFER_RESEND_LABEL_FIRST : RESEND_LABEL_FIRST)}
                        </Button>
                    </Tooltip>
                );
            case "copy":
                return (
                    <Button
                        key={action}
                        type={buttonType}
                        size="small"
                        disabled={isLoading}
                        onClick={handleCopy}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            case "cancel":
                return (
                    <Button
                        key={action}
                        type={buttonType === "primary" ? "primary" : "default"}
                        danger={buttonType !== "primary"}
                        size="small"
                        disabled={isLoading}
                        onClick={handleCancel}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            case "refresh":
                return (
                    <Button
                        key={action}
                        type={buttonType}
                        size="small"
                        disabled={isLoading}
                        onClick={handleRefresh}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            case "captureFull":
                return (
                    <Button
                        key={action}
                        type={buttonType}
                        size="small"
                        disabled={isLoading}
                        onClick={handleCaptureFull}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            case "capturePartial":
                return (
                    <Button
                        key={action}
                        type={buttonType}
                        size="small"
                        disabled={isLoading}
                        onClick={handleCapturePartialOpen}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            case "refundPartial":
            case "refundMore":
                return (
                    <Button
                        key={action}
                        type={buttonType}
                        size="small"
                        disabled={isLoading}
                        onClick={handleRefundOpen}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            case "newAttempt":
                return (
                    <Button
                        key={action}
                        type={buttonType}
                        size="small"
                        disabled={isLoading}
                        onClick={handleNewAttempt}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            case "openDashboard":
                return (
                    <Button
                        key={action}
                        type="link"
                        size="small"
                        disabled={isLoading}
                        onClick={handleOpenDashboard}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            case "showChargebacks":
                return (
                    <Button
                        key={action}
                        type="link"
                        size="small"
                        disabled={isLoading}
                        onClick={handleShowChargebacks}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            case "recordExternalPayment":
                return (
                    <Button
                        key={action}
                        type="link"
                        size="small"
                        disabled={isLoading}
                        onClick={handleRecordOpen}
                        data-testid={dataTestId}
                    >
                        {label}
                    </Button>
                );
            default:
                return null;
        }
    }

    // ─── primaryOnly-Variante (Aktionsspalte der Posten-Zeile) ────────────
    // Genau EINE Aktion: die erste Primäraktion des Mappings. Für alle
    // Erstattungs-Varianten (refundPartial/refundMore) wird ein einziger
    // "Erstatten"-Button gerendert, der das Refund-Modal öffnet.
    function renderPrimaryOnly(): React.ReactNode {
        const primaryKey = actions.primary.length > 0 ? actions.primary[0] : null;
        if (!primaryKey) {
            return null;
        }

        if (primaryKey === "refundPartial" || primaryKey === "refundMore") {
            return (
                <Button
                    type="link"
                    size="small"
                    disabled={isLoading}
                    onClick={handleRefundOpen}
                    data-testid="mollie-action-refund"
                >
                    {"Erstatten"}
                </Button>
            );
        }

        if (primaryKey === "resend") {
            return (
                <Button
                    type="primary"
                    size="small"
                    disabled={isLoading}
                    onClick={handleResend}
                    data-testid="mollie-action-resend"
                >
                    {isBanktransfer ? BANKTRANSFER_RESEND_LABEL_REPEAT : RESEND_LABEL_REPEAT}
                </Button>
            );
        }

        if (primaryKey === "refresh") {
            return (
                <Button
                    size="small"
                    disabled={isLoading}
                    onClick={handleRefresh}
                    data-testid="mollie-action-refresh"
                >
                    {ACTION_LABELS.refresh}
                </Button>
            );
        }

        if (primaryKey === "newAttempt") {
            return (
                <Button
                    size="small"
                    disabled={isLoading}
                    onClick={handleNewAttempt}
                    data-testid="mollie-action-newAttempt"
                >
                    {isBanktransfer ? "Neuer Zahlungsversuch" : "Neuer Zahlungslink"}
                </Button>
            );
        }

        return renderActionButton(primaryKey, "primary");
    }

    return (
        <div className={css.actionBar} data-testid="mollie-action-bar" data-variant={isPrimaryOnly ? "primaryOnly" : "full"}>
            {isPrimaryOnly ? (
                renderPrimaryOnly()
            ) : (
                <>
                    <div className={css.actionBarPrimaryGroup}>
                        {actions.primary.map((a) => renderActionButton(a, "primary"))}
                    </div>
                    <div className={css.actionBarSecondaryGroup}>
                        {actions.secondary.map((a) => renderActionButton(a, "default"))}
                    </div>
                </>
            )}

            {/* AntD 4 Pitfall 1: `visible`, NICHT `open`. */}
            <Modal
                title="Teilweise erfassen"
                visible={capturePartialOpen}
                onOk={handleCapturePartialConfirm}
                onCancel={() => setCapturePartialOpen(false)}
                okText="Erfassen"
                cancelText="Abbrechen"
                destroyOnClose
                data-testid="capture-partial-modal"
            >
                <div>
                    <p>Betrag der Erfassung in EUR:</p>
                    <InputNumber
                        autoFocus
                        min={0.01}
                        max={attempt.amount || undefined}
                        step={0.01}
                        precision={2}
                        value={capturePartialAmount as number | undefined}
                        onChange={(value) =>
                            setCapturePartialAmount(value == null ? null : Number(value))
                        }
                        style={{width: "100%"}}
                        data-testid="capture-partial-amount-input"
                    />
                </div>
            </Modal>

            <Modal
                title="Erstatten"
                visible={refundModalOpen}
                onOk={handleRefundConfirm}
                onCancel={closeRefundModal}
                okText="Erstatten"
                okType="danger"
                cancelText="Abbrechen"
                destroyOnClose
                data-testid="refund-partial-modal"
            >
                <div>
                    {/* Doppel-Gutschrift-Schutz: prominente Warnung bei stornierter
                        (ggf. umgebuchter) Buchung — für voll UND teilweise. Der Nutzer
                        muss die rote Warnung passieren und den danger-Button klicken. */}
                    {isBookingCanceled && (
                        <p
                            style={{color: "#cf1322", fontWeight: 600, marginBottom: 12}}
                            data-testid="refund-canceled-warning"
                        >
                            {CANCELED_REFUND_DOUBLE_CREDIT_WARNING}
                        </p>
                    )}
                    {/* Bezugsbasis: Erstatten und Einbehalten ergeben zusammen den Restbetrag. */}
                    <div
                        style={{display: "flex", justifyContent: "space-between", gap: 12, background: "#fafafa", border: "1px solid #f0f0f0", padding: "6px 10px", marginBottom: 12, fontSize: 13}}
                        data-testid="refund-basis"
                    >
                        <span>{`Bezahlt ${renderAmount({amount: refundSummary.amount})}`}</span>
                        <span>{`Bereits erstattet ${renderAmount({amount: totalRefunded})}`}</span>
                        <strong>{`Restbetrag ${renderAmount({amount: remaining})}`}</strong>
                    </div>
                    {/* Stornogebühr gehört ins Feld „Einbehalten“, nicht in den Grund. */}
                    <RefundAmountFields
                        autoFocus
                        remaining={remaining}
                        refundAmount={refundAmount}
                        onChange={setRefundAmount}
                    />
                    <Button
                        type="link"
                        size="small"
                        style={{paddingLeft: 0}}
                        onClick={() => setRefundAmount(remaining)}
                        data-testid="refund-take-remaining"
                    >
                        {`Alles erstatten (${renderAmount({amount: remaining})})`}
                    </Button>

                    {/* Grund (optional) — max. 500 Zeichen (DB-Spalte varchar(500)). */}
                    <p style={{marginTop: 12, marginBottom: 4}}>Grund (optional):</p>
                    <Input.TextArea
                        rows={2}
                        maxLength={500}
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        placeholder="z.B. Kulanz, Doppelzahlung, Kursausfall …"
                        data-testid="refund-reason-input"
                    />

                    {/* Bucher-Benachrichtigung (Default AN). */}
                    <div style={{marginTop: 12}}>
                        <Checkbox
                            checked={refundNotifyBooker}
                            onChange={(e) => setRefundNotifyBooker(e.target.checked)}
                            data-testid="refund-notify-booker-checkbox"
                        >
                            Bucher per E-Mail über die Erstattung informieren
                        </Checkbox>
                    </div>
                </div>
            </Modal>

            <RecordExternalPaymentModal
                visible={recordOpen}
                openAmount={Number(attempt.amount || 0)}
                loading={isLoading}
                onCancel={() => setRecordOpen(false)}
                onConfirm={handleRecordConfirm}
            />
        </div>
    );
};

export default ActionBar;
